import classNames from 'classnames'

import { QUERY_KEY_MANAGEMENT_SEARCH_UNITS } from '@apps-common/apis/management/unit'
import { useManagementUpdateSingleCancelledNonProcessed } from '@apps-common/apis/management/unit-cancelled'
import { CopyWithText, ImageComponent } from '@apps-common/components/common'
import { VertSpacerLg, VertSpacerXS } from '@apps-common/components/layout'
import { ConfirmModal, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_ACTIVATION_ALLOWED_PRODUCTS } from '@apps-common/config/product-codes'
import {
  unitIsActiveManagement,
  unitIsCancelledManagement,
  unitIsInactiveManagement,
  unitIsPendingCancellationManagement
} from '@apps-common/helpers/unit-helpers'
import { IAppNavigationOptions } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { IManagementUnitSearchModel } from '@apps-common/types/management/unit'
import {
  ROUTE_ORDER_DETAILS,
  ROUTE_UNIT_ACTIVATION_DETAILS,
  ROUTE_UNIT_DETAILS,
  ROUTE_UNIT_MANAGE
} from '@apps-radionet-portal-root/src/config/private-routes'

import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { navigateActivationWithUnitDetails } from '../../../pages/activations/navigateActivationWithUnitDetails'

interface IProps {
  bgClasses: string
  textClasses: string
  iconClasses: string
  bgSecondaryClasses: string
  textSecondaryClasses: string
  bgTertiaryClasses: string
  unit: IManagementUnitSearchModel
  buttonText?: string
  navigateForward: (opts: IAppNavigationOptions) => void
  callback?: (unit: IManagementUnitSearchModel) => void
}

export const ManagementUnitCard: React.FC<IProps> = (props) => {
  const { bgClasses, textClasses, bgSecondaryClasses, textSecondaryClasses, unit, navigateForward, callback } = props

  const {
    status,
    unitId,
    accountNo,
    imei,
    serialNo,
    mobileNo,
    monitoringCentre,
    bureau,
    productId,
    productImageUrl,
    pendingCancellationDate,
    cancellationDate,
    cancellationProcessed,
    siteName,
    orderId,
    unitActivationRequestID
  } = unit

  const { isPending: markProcessedPending, mutate: markProcessed } = useManagementUpdateSingleCancelledNonProcessed(true, () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNITS() })
  })

  const isInactive = unitIsInactiveManagement(unit)
  const isActive = unitIsActiveManagement(unit)
  const isPendingCancellation = unitIsPendingCancellationManagement(unit)
  const isCancelled = unitIsCancelledManagement(unit)
  const isActiveLike = isActive || isPendingCancellation
  const variant = isInactive ? 'inactive' : isCancelled ? 'cancelled' : 'active'
  const showInactive = variant === 'inactive'
  const showCancelled = variant === 'cancelled'
  const showActive = variant === 'active'

  const mcName = monitoringCentre || 'Monitoring Centre'
  const mcLabel = showInactive
    ? mcName === bureau || mcName?.toLowerCase()?.includes('radionet')
      ? bureau
      : `${mcName} | ${bureau}`
    : bureau === mcName
      ? mcName
      : `${mcName} | ${bureau}`

  const handleManage = (e: React.MouseEvent) => {
    e?.stopPropagation()
    if (callback) {
      callback(unit)
    } else {
      navigateForward({ routeUrl: ROUTE_UNIT_MANAGE.buildPath(status, unitId), route: ROUTE_UNIT_MANAGE })
    }
  }

  const handleDetails = (e: React.MouseEvent) => {
    e?.stopPropagation()
    navigateForward({ routeUrl: ROUTE_UNIT_DETAILS.buildPath(status, unitId), route: ROUTE_UNIT_DETAILS })
  }

  const handleOrder = (e: React.MouseEvent) => {
    e?.stopPropagation()
    if (orderId) {
      navigateForward({ routeUrl: ROUTE_ORDER_DETAILS.buildPath(orderId), route: ROUTE_ORDER_DETAILS })
    }
  }

  const handleActivation = (e: React.MouseEvent) => {
    e?.stopPropagation()
    if (unitActivationRequestID) {
      navigateForward({
        routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(unitActivationRequestID),
        route: ROUTE_UNIT_ACTIVATION_DETAILS
      })
    } else {
      navigateActivationWithUnitDetails(navigateForward, unitId, serialNo, imei)
    }
  }

  return (
    <button type='button' className={classNames('rounded-[35px] p-5 min-w-[160px] overflow-hidden', bgClasses)} onClick={handleManage}>
      <div className='flex items-stretch justify-between gap-[10px] h-full'>
        <div className='grow'>
          <div className='flex items-center justify-start'>
            <h4
              className={classNames(
                'jost-light text-[0.85rem] text-ellipsis whitespace-nowrap overflow-hidden flex items-center justify-start gap-[5px]',
                textClasses
              )}
            >
              {mcLabel}
            </h4>
          </div>
          <div className='flex items-center justify-between gap-[10px]'>
            <div className='flex items-center justify-start gap-[5px]'>
              <div
                className={classNames(
                  'px-4 py-2 h-[20px] rounded-[15px] flex items-center justify-start gap-[10px] min-w-0',
                  bgSecondaryClasses
                )}
              >
                <commonIcons.processor className={textSecondaryClasses} />
                <h5 className={classNames('jost-light text-[0.85rem] uppercase', textClasses)}>
                  {accountNo || <span className='text-[0.85em]'>None</span>}
                </h5>
              </div>
            </div>
            <div
              className={classNames(
                'w-[45px] h-[45px] rounded-[23px] flex items-center justify-center overflow-hidden',
                bgSecondaryClasses
              )}
            >
              <ImageComponent
                src={productImageUrl}
                alt={`Product ${accountNo}`}
                imageClasses={classNames('max-w-[30px]')}
                errorIconClasses='text-[1.2rem]'
              />
            </div>
          </div>
          <VertSpacerXS />

          {showInactive && (
            <div className='flex flex-col items-start justify-start gap-[5px] pl-[5px]'>
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={textClasses} />
              <CopyWithText textValue='S/N' value={serialNo} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
            </div>
          )}

          {showActive && (
            <div className='flex flex-col items-start justify-start gap-[5px]'>
              <CopyWithText textValue={siteName} noTextValue='<No site info>' value={siteName} className={textClasses} />
              <CopyWithText textValue='MOB' value={mobileNo} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              {isActiveLike && pendingCancellationDate && (
                <p className={classNames('jost text-[0.75rem] text-ellipsis whitespace-nowrap overflow-hidden', textClasses)}>
                  <span className='bg-warning text-on-warning rounded-md px-[4px]'>Cancels on</span>{' '}
                  {toLocalTimestampStr(pendingCancellationDate, 'DD/MM/YYYY')}
                </p>
              )}
            </div>
          )}

          {showCancelled && (
            <div className='flex flex-col items-start justify-start gap-[5px] pl-[5px]'>
              <CopyWithText textValue={siteName} noTextValue='<No site info>' value={siteName} className={textClasses} />
              <CopyWithText textValue='MOB' value={mobileNo} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              <CopyWithText textValue='IMEI' value={imei} showCopyValue className={classNames(textClasses, '!text-[0.75rem]')} />
              <p
                className={classNames(
                  'jost text-[0.75rem] text-ellipsis whitespace-nowrap overflow-hidden flex items-center justify-start gap-[6px]',
                  textClasses
                )}
              >
                <span className='bg-surface-2 text-text border border-border rounded-md px-[4px]'>Cancelled on</span>{' '}
                {toLocalTimestampStr(cancellationDate || '', 'DD/MM/YYYY')}
                <div
                  className={classNames(
                    'rounded-md px-[5px]',
                    cancellationProcessed && 'bg-error text-error-content',
                    !cancellationProcessed && 'bg-warning text-warning-content'
                  )}
                >
                  {cancellationProcessed ? 'Processed' : 'Pending'}
                </div>
              </p>
            </div>
          )}

          <VertSpacerLg />
          <div className='flex items-center justify-between gap-[10px]'>
            <div className='flex items-stretch justify-start gap-x-2'>
              {showInactive && !callback && UNIT_ACTIVATION_ALLOWED_PRODUCTS.includes(productId) && (
                <button
                  type='button'
                  className={classNames(
                    'relative action-success flex items-center justify-center gap-[5px] w-[35px] h-[35px] rounded-full',
                    !unitActivationRequestID && 'action-success',
                    unitActivationRequestID && 'action-warning'
                  )}
                  onClick={handleActivation}
                >
                  <commonIcons.srActivations className='text-[0.9rem]' />
                </button>
              )}

              {orderId && (
                <button
                  type='button'
                  className={classNames('action-success flex items-center justify-center gap-[5px] w-[35px] h-[35px] rounded-full')}
                  onClick={handleOrder}
                >
                  <commonIcons.sbOrders className='text-[0.9rem]' />
                </button>
              )}

              {showCancelled && !cancellationProcessed && (
                <ModalButton
                  component={ConfirmModal}
                  disabled={markProcessedPending}
                  classes=''
                  componentProps={{
                    text: 'Mark processed',
                    description: 'Confirm you would like to mark this sim as processed?'
                  }}
                  onClickCallback={(e: any) => e?.stopPropagation()}
                  confirmCallback={() => markProcessed({ unitId })}
                >
                  <div className={classNames('action-error flex items-center justify-center gap-[5px] w-[35px] h-[35px] rounded-full')}>
                    <commonIcons.processSimCancellation className='text-[0.9rem]' />
                  </div>
                </ModalButton>
              )}

              <button
                type='button'
                className={classNames('action-info flex items-center justify-center gap-[5px] w-[35px] h-[35px] rounded-full')}
                onClick={handleDetails}
              >
                <commonIcons.details className='text-[0.9rem]' />
              </button>
            </div>
            <button
              type='button'
              className={classNames(
                'action-brand min-w-[140px] flex items-center justify-between gap-[5px] jost text-[0.8rem] !pl-[15px] !p-[5px] h-[35px] rounded-full'
              )}
              onClick={handleManage}
            >
              Manage
              <div className='bg-brand text-on-brand w-[26px] h-[26px] rounded-[13px] flex items-center justify-center'>
                <commonIcons.arrowAngled className='text-[0.9rem]' />
              </div>
            </button>
          </div>
        </div>
      </div>
    </button>
  )
}
